import { createAsyncThunk } from '@reduxjs/toolkit';
import { apiServiceInstanceDashboard, apiServiceInstanceDashboardWithWaitTime } from '../apiUtils';
import {
  FetchThematicGroupingPayload,
  CreateThemeElementPayload,
  RegenerateThemePayload,
  ThemeGroup,
  ThematicFilterObject,
} from '@/types/thematicAnalysis';

// Delete a theme element
export const deleteThemeElement = createAsyncThunk(
  'thematicAnalysis/deleteElement',
  async (payload: {
    projectId: number;
    batchId: number;
    reportId: number;
    reportPageId: number;
    reportElementId: number;
  }) => {
    const res = await apiServiceInstanceDashboard.delete('/report/elements/', {
      data: payload,
    });
    return { ...res.data, deletedElementId: payload.reportElementId };
  },
);

// Reorder a theme element
export const reorderThemeElement = createAsyncThunk(
  'thematicAnalysis/reorderElement',
  async (payload: {
    projectId: number;
    batchId: number;
    reportId: number;
    reportPageId: number;
    reportElementId: number;
    orderId: number;
  }) => {
    const res = await apiServiceInstanceDashboard.post(
      '/report/reorderelement/',
      payload,
    );
    return res.data;
  },
);

// Merge two themes into one
export const mergeThemes = createAsyncThunk(
  'thematicAnalysis/mergeThemes',
  async (payload: {
    projectId: number;
    batchId: number;
    reportId: number;
    reportPageId: number;
    sourceElementId: number;
    targetElementId: number;
    mergedThemeName: string;
    mergedQuestionIds: number[];
    themeId?: string;
    userPreference?: string;
  }) => {
    // Update target element with merged questions
    const updateRes = await apiServiceInstanceDashboard.put('/report/elements/', {
      projectId: payload.projectId,
      batchId: payload.batchId,
      reportId: payload.reportId,
      reportPageId: payload.reportPageId,
      reportElementId: payload.targetElementId,
      filterObject: {
        batchId: payload.batchId,
        themeId: payload.themeId || `merged-${Date.now()}`,
        themeName: payload.mergedThemeName,
        questionIds: payload.mergedQuestionIds,
        userPreference: payload.userPreference || '',
        age: [],
        gender: [],
        city: [],
        state: [],
        country: [],
      },
      filterType: 'thematic_analysis',
      title: payload.mergedThemeName,
      titleStyle: {},
      description: '',
      descriptionStyle: {},
      notes: '',
      notesStyle: {},
      elementType: 'thematic_theme',
      elementStyle: {},
      layoutType: 'full_width',
      layoutStyle: {},
    });

    return {
      ...updateRes.data,
      mergedElementId: payload.targetElementId,
      sourceElementId: payload.sourceElementId,
    };
  },
);

// Fetch AI-suggested thematic groupings
export const fetchThematicGrouping = createAsyncThunk(
  'thematicAnalysis/fetchGrouping',
  async (payload: FetchThematicGroupingPayload) => {
    const res = await apiServiceInstanceDashboardWithWaitTime.post(
      '/report/consumer-journey/grouping/',
      {
        ...payload,
        groupingType: 'thematic',
      },
    );
    return res.data;
  },
);

// Create a single theme element
export const createThemeElement = async (payload: CreateThemeElementPayload) => {
  const filterObject: ThematicFilterObject = {
    batchId: payload.batchId,
    themeId: payload.theme.themeId,
    themeName: payload.theme.themeName,
    questionIds: payload.theme.questions.map((q) => q.questionId),
    userPreference: payload.theme.userPreference || '',
    age: [],
    gender: [],
    city: [],
    state: [],
    country: [],
  };

  const res = await apiServiceInstanceDashboard.post('/report/elements/', {
    projectId: payload.projectId,
    batchId: payload.batchId,
    reportId: payload.reportId,
    reportPageId: payload.reportPageId,
    orderId: payload.orderId,
    filterType: 'thematic_analysis',
    filterObject,
    title: payload.theme.themeName,
    titleStyle: {},
    notes: '',
    notesStyle: {},
    elementType: 'thematic_theme',
    elementStyle: {},
    layoutType: 'full_width',
    layoutStyle: {},
  });

  return res.data;
};

// Create all theme elements in parallel
export const createAllThemeElements = createAsyncThunk(
  'thematicAnalysis/createAllElements',
  async (
    payload: {
      projectId: number;
      batchId: number;
      reportId: number;
      reportPageId: number;
      themes: ThemeGroup[];
      userId: number;
      session?: string;
      startOrderId?: number;
    },
  ) => {
    const startOrder = payload.startOrderId || 1;

    // Create elements in parallel
    const promises = payload.themes.map((theme, index) =>
      createThemeElement({
        projectId: payload.projectId,
        batchId: payload.batchId,
        reportId: payload.reportId,
        reportPageId: payload.reportPageId,
        theme,
        orderId: startOrder + index,
        userId: payload.userId,
        session: payload.session,
      }),
    );

    const responses = await Promise.all(promises);
    return responses;
  },
);

// Regenerate AI analysis for a theme
export const regenerateThemeAnalysis = createAsyncThunk(
  'thematicAnalysis/regenerate',
  async (payload: RegenerateThemePayload) => {
    const res = await apiServiceInstanceDashboard.post(
      '/report/thematic-analysis/regenerate/',
      payload,
    );
    return res.data;
  },
);

// Fetch a single theme element data
export const fetchThemeElement = async (payload: {
  projectId: number;
  batchId: number;
  reportId: number;
  reportPageId: number;
  reportElementId: number;
  userId: number;
  session?: string;
}) => {
  const res = await apiServiceInstanceDashboard.post(
    '/report/getelements/',
    payload,
  );
  return res.data;
};

// Fetch all theme elements for a page
export const fetchAllThemeElements = createAsyncThunk(
  'thematicAnalysis/fetchAllElements',
  async (payload: {
    projectId: number;
    batchId: number;
    reportId: number;
    reportPageId: number;
    elementIds: number[];
    userId: number;
    session?: string;
  }) => {
    const promises = payload.elementIds.map((elementId) =>
      fetchThemeElement({
        projectId: payload.projectId,
        batchId: payload.batchId,
        reportId: payload.reportId,
        reportPageId: payload.reportPageId,
        reportElementId: elementId,
        userId: payload.userId,
        session: payload.session,
      }),
    );

    const responses = await Promise.all(promises);
    return responses;
  },
);
